// @flow

import React from 'react';
import { isPlainObject } from 'lodash';

import { base } from 'app/developer-v3/components/PartnerSupportForm/utils/tree';

import type { NodeDefinition } from 'app/developer-v3/components/PartnerSupportForm/utils/tree/types';

import Help from './Help';
import { type HelpNodeOptions } from './types';

const help = (
  options: HelpNodeOptions & { skipInterstitial?: boolean } = {
    contact: [],
  }
): NodeDefinition => {
  const hasContactFields =
    Array.isArray(options.contact) && options.contact.length > 0;

  const node = base({
    id: 'help',
    isRequired: hasContactFields,
    isDataValid: data => {
      if (!hasContactFields) {
        return true;
      }
      const contact = options.contact || []; // flow
      const contactData = isPlainObject(data) ? data : {}; // flow
      return !contact.some(
        ({ id, isRequired }) => isRequired && !contactData[id]
      );
    },
    parseData: data => {
      if (!isPlainObject(data)) {
        return null;
      }

      // skip feedback via querystring
      if (typeof data.contact === 'string') {
        data.contact = data.contact === 'yes';
      }

      return data;
    },
    render: props => <Help {...props} {...options} />,
    // We could potentially allow export to return an array and flatten here instead of in backend, but we'd risk id collisions.
    export: data => {
      const result = {
        id: node.id,
        data,
        formatted: [],
      };

      if (hasContactFields) {
        const contact = options.contact || []; // flow
        const contactData = isPlainObject(data) ? data : {}; // flow

        // Check if this is a Solution Partner form with additional fields
        const hasPortalEmail = contact.some(
          field => field.id === 'portal_email'
        );
        const hasCompanyName = contact.some(
          field => field.id === 'company_name'
        );
        const hasLeadEmail = contact.some(field => field.id === 'lead_email');

        // Append additional fields to message for Solution Partner forms
        if (
          (hasPortalEmail || hasCompanyName || hasLeadEmail) &&
          contactData.message
        ) {
          let additionalInfo = '\n\n---  \n**Additional Information:**';

          if (contactData.portal_email) {
            additionalInfo += `  \n**Portal Email:** ${contactData.portal_email}`;
          }

          if (contactData.company_name) {
            additionalInfo += `  \n**Company Name:** ${contactData.company_name}`;
          }

          if (contactData.lead_email) {
            additionalInfo += `  \n**Lead Email:** ${contactData.lead_email}`;
          }

          // Only append if we have at least one field
          if (additionalInfo !== '\n\n---  \n**Additional Information:**') {
            contactData.message += additionalInfo;
          }
        }

        contact.forEach(({ id, label, type, value, zendeskCustomFieldId }) => {
          const itemData = contactData[id];
          // Include fields with data, or hidden fields that have a predefined value
          if (itemData || (type === 'hidden' && value)) {
            const dataValue = itemData || value; // Use 'value' for hidden fields
            const formattedItemData =
              type === 'image' && typeof itemData === 'string'
                ? itemData.replace(/\s+/g, '<br />')
                : null;
            result.formatted.push({
              id,
              label,
              data: dataValue,
              formatted: formattedItemData,
              ...(zendeskCustomFieldId && { zendeskCustomFieldId }),
            });
          }
        }, []);
      }

      return result;
    },
  });

  return node;
};

export default help;
